import { useFetch } from "@hyper-fetch/react";

import { useSiteConfig } from "./use-site-config";
import { sdk } from "@/api/sdk";

type DirectoryQuery = {
  /** First day of the grid. Left out, the backend starts today. */
  from?: string;
  days: number;
  /** Narrow the coaches and their times to one booking service. */
  sessionTypeId?: string;
};

/**
 * Everything the signed-out site may show: the studio, its services, and every coach with
 * their open times. Answers `null` while it loads and when public booking is switched off, so
 * pages render their static parts either way.
 *
 * Which studio it asks about comes from the site config, not from the caller: inside /<slug> it
 * is that trainer, and every section on the page reads the same cache. Without a slug it falls
 * back to the pack's own single-business endpoint, which is what a one-studio install has.
 *
 * The request only runs in the browser, so the default week starts out as what the server read
 * with the page: the server's HTML holds the services and coaches, and the browser's own answer
 * replaces them when it comes. Another week or one service has to wait for that answer.
 */
export const usePublicDirectory = ({ from, days, sessionTypeId }: DirectoryQuery) => {
  const { slug, directory: readWithPage } = useSiteConfig();

  const span = { ...(from ? { from } : {}), ...(sessionTypeId ? { sessionTypeId } : {}) };

  const request = slug
    ? sdk.t.$slug.$get.setParams({ slug }).setQueryParams({ days, ...span })
    : // The pack's own endpoint types `days` as the string a query string carries, and coerces it.
      sdk.bookingPublic.directory.$get.setQueryParams({ days: String(days), ...span });

  const { data, loading, error } = useFetch(request, { dependencies: [slug, from, days, sessionTypeId] });

  const directory = data ?? (from || sessionTypeId ? null : readWithPage);

  return {
    directory,
    /** First load only: a refetch keeps the previous week on screen. */
    loading: loading && !directory,
    /** Public booking is off, or the request failed. Either way there is nothing to book online. */
    unavailable: Boolean(error) && !directory,
  };
};
